// UK Template S: a classic UK payslip in the common Sage arrangement. The
// employer and "PAYSLIP" up top; a strip with the employee, payroll number,
// NI number, tax code, NI letter and payment method; Payments and
// Deductions tables side by side with their totals; This Period and Year to
// Date summaries; a net pay band. Helvetica, grey bands, thin rules, pounds,
// DD/MM/YYYY, year to date from 6 April.

import { describePayment } from "./ukPaymentMethods";

export function generateUkTemplateS(doc, data, pageWidth, pageHeight, margin) {
  const {
    formData, hours, overtime, commission = 0, regularPay, overtimePay, grossPay,
    payeTax, nationalInsurance, studentLoan, totalTax, rate,
    startDate, endDate, payDate, stubNum,
    ytdGrossPay = grossPay, ytdPayeTax = payeTax, ytdNationalInsurance = nationalInsurance, ytdStudentLoan = studentLoan,
    ytdRegularPay = regularPay, ytdOvertimePay = overtimePay, ytdCommission = 0,
    deductionsData = [], totalDeductions = 0, contributionsData = [], totalContributions = 0,
    ytdDeductions = 0, ytdContributions = 0, ytdPayPeriods = 1,
    payType = "hourly", isContractor = false,
    taxCode = "", niNumber = "", niCategory = "A", taxPeriod = "", taxYear = "",
    periodCheckNumber = "", periodMemo = "", logoDataUrl,
  } = data;

  const grey = [230, 230, 230];
  const rule = [150, 150, 150];
  const black = [20, 20, 20];
  const money = (n) => Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const pounds = (n) => `£${money(n)}`;
  const fmtDate = (date) => {
    const d = date instanceof Date ? date : new Date(typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date) ? `${date}T12:00:00` : date);
    if (isNaN(d.getTime())) return "";
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  };
  const text = (t, x, yy, opts = {}) => {
    doc.setFont("helvetica", opts.bold ? "bold" : "normal");
    doc.setFontSize(opts.size || 8.5);
    doc.setTextColor(...(opts.color || black));
    doc.text(String(t ?? ""), x, yy, opts.align ? { align: opts.align } : undefined);
  };
  const band = (x, yy, w, h) => { doc.setFillColor(...grey); doc.rect(x, yy, w, h, "F"); };
  const line = (x1, y1, x2, y2, width = 0.4) => { doc.setDrawColor(...rule); doc.setLineWidth(width); doc.line(x1, y1, x2, y2); };

  // the slip adds up on its own: every listed deduction comes off gross pay
  const otherDeductions = (Number(totalDeductions) || 0) + (Number(totalContributions) || 0);
  const totalAllDeductions = (Number(totalTax) || 0) + otherDeductions;
  const net = (Number(grossPay) || 0) - totalAllDeductions;
  const ytdTotalTax = (Number(ytdPayeTax) || 0) + (Number(ytdNationalInsurance) || 0) + (Number(ytdStudentLoan) || 0);
  const ytdOther = (Number(ytdDeductions) || 0) + (Number(ytdContributions) || 0);
  const ytdNet = (Number(ytdGrossPay) || 0) - ytdTotalTax - ytdOther;
  const hourly = payType !== "salary";
  const payslipNo = periodCheckNumber || String(1000 + ((stubNum || 1) * 7919) % 9000);
  const payrollNo = formData.employeeId || "";
  // how it was paid: the strip and the net pay band follow the method, and
  // only a bank method names an account (the payslip number is the cheque's)
  const pm = describePayment(formData.paymentMethod, { bankLast4: formData.bank || "", chequeNo: payslipNo, paidOn: fmtDate(payDate) });

  const M = 36;
  const W = pageWidth - 2 * M;
  const T = 40;
  const R = M + W;

  // ── header: employer left, PAYSLIP right ──
  let y = T;
  let hx = M + 10;
  if (logoDataUrl) {
    try {
      const fmt = /^data:image\/png/i.test(logoDataUrl) ? "PNG" : "JPEG";
      doc.addImage(logoDataUrl, fmt, M + 10, y + 8, 54, 26, undefined, "FAST");
      hx = M + 72;
    } catch { /* no logo */ }
  }
  text(formData.company || "Employer Name", hx, y + 18, { bold: true, size: 11 });
  text(formData.companyAddress || "1 Business Park", hx, y + 30);
  text(`${formData.companyCity || "Town"} ${formData.companyPostcode || "SW1A 1AA"}`, hx, y + 41);
  if (formData.companyPhone) text(formData.companyPhone, hx, y + 52);

  text("PAYSLIP", R - 10, y + 22, { bold: true, size: 16, align: "right" });
  text(`Pay Date  ${fmtDate(payDate)}`, R - 10, y + 36, { align: "right", size: 9 });
  text(`Tax Period  ${taxPeriod || ""}${taxYear ? `    Tax Year  ${taxYear}` : ""}`, R - 10, y + 48, { align: "right", size: 9 });
  text(`Payslip No  ${payslipNo}`, R - 10, y + 60, { align: "right", size: 9 });

  // ── employee strip ──
  y = T + 70;
  band(M, y, W, 26);
  const cells = [
    ["Employee", formData.name || "Employee Name", 150],
    ["Payroll No", payrollNo || "", 70],
    ["NI Number", niNumber || formData.niNumber || "", 95],
    ["Tax Code", taxCode || formData.taxCode || "1257L", 60],
    ["NI Letter", isContractor ? "n/a" : (niCategory || "A"), 55],
    ["Payment Method", pm.label, 110],
  ];
  let cx = M;
  cells.forEach(([lab, val, w], i) => {
    text(lab, cx + 6, y + 9, { bold: true, size: 6.5, color: [90, 90, 90] });
    text(val, cx + 6, y + 20, { size: 8.5 });
    if (i > 0) line(cx, y, cx, y + 26, 0.3);
    cx += w;
  });
  line(M, y + 26, R, y + 26, 0.6);

  // ── employee address, pay period ──
  y = T + 106;
  text(formData.name || "Employee Name", M + 6, y, { bold: true });
  text(formData.address || "1 High Street", M + 6, y + 11);
  text(`${formData.city || "Town"} ${formData.postcode || "SW1A 1AA"}`, M + 6, y + 22);
  text(`Pay Period  ${fmtDate(startDate)}  to  ${fmtDate(endDate)}`, R - 10, y, { align: "right", size: 9 });
  if (periodMemo) text(periodMemo, R - 10, y + 12, { align: "right", size: 8 });

  // ── payments and deductions ──
  y = T + 140;
  const rowH = 11;
  const minRows = 7;
  const leftW = 300;
  const rightX = M + leftW + 12;
  const rightW = R - rightX;
  const pCols = { desc: 130, hours: 50, rate: 55, amt: leftW - 235 };

  band(M, y, leftW, 14);
  text("Payments", M + 6, y + 10, { bold: true });
  text("Hours", M + pCols.desc + pCols.hours - 4, y + 10, { bold: true, align: "right" });
  text("Rate", M + pCols.desc + pCols.hours + pCols.rate - 4, y + 10, { bold: true, align: "right" });
  text("Amount", M + leftW - 6, y + 10, { bold: true, align: "right" });
  band(rightX, y, rightW, 14);
  text("Deductions", rightX + 6, y + 10, { bold: true });
  text("Amount", R - 6, y + 10, { bold: true, align: "right" });

  const payments = [];
  if (hourly) {
    payments.push(["Basic Pay", Number(hours || 0).toFixed(2), money(rate), money(regularPay)]);
    if (!isContractor && Number(overtime) > 0) payments.push(["Overtime", Number(overtime).toFixed(2), money(rate * 1.5), money(overtimePay)]);
  } else {
    payments.push(["Salary", "", "", money(regularPay)]);
  }
  if (Number(commission) > 0) payments.push(["Commission", "", "", money(commission)]);

  const deductions = [];
  if (!isContractor) {
    deductions.push(["PAYE Tax", money(payeTax)]);
    deductions.push(["National Insurance", money(nationalInsurance)]);
    if (Number(studentLoan) > 0) deductions.push(["Student Loan", money(studentLoan)]);
  }
  contributionsData.forEach((c) => deductions.push([String(c.name || c.type || "Pension").substring(0, 28), money(c.currentAmount || 0)]));
  deductionsData.forEach((d) => deductions.push([String(d.name || d.type || "Deduction").substring(0, 28), money(d.currentAmount || 0)]));

  const rows = Math.max(minRows, payments.length, deductions.length);
  let ry = y + 14;
  for (let i = 0; i < rows; i++) {
    const p = payments[i];
    if (p) {
      text(p[0], M + 6, ry + 8);
      text(p[1], M + pCols.desc + pCols.hours - 4, ry + 8, { align: "right" });
      text(p[2], M + pCols.desc + pCols.hours + pCols.rate - 4, ry + 8, { align: "right" });
      text(p[3], M + leftW - 6, ry + 8, { align: "right" });
    }
    const d = deductions[i];
    if (d) {
      text(d[0], rightX + 6, ry + 8);
      text(d[1], R - 6, ry + 8, { align: "right" });
    }
    ry += rowH;
  }
  // frames and the totals beneath each table
  line(M, y, M, ry, 0.5); line(M + leftW, y, M + leftW, ry, 0.5); line(M, ry, M + leftW, ry, 0.5);
  line(rightX, y, rightX, ry, 0.5); line(R, y, R, ry, 0.5); line(rightX, ry, R, ry, 0.5);
  band(M, ry, leftW, 14);
  text("Total Gross Pay", M + 6, ry + 10, { bold: true });
  text(pounds(grossPay), M + leftW - 6, ry + 10, { bold: true, align: "right" });
  band(rightX, ry, rightW, 14);
  text("Total Deductions", rightX + 6, ry + 10, { bold: true });
  text(pounds(totalAllDeductions), R - 6, ry + 10, { bold: true, align: "right" });

  // ── this period / year to date ──
  y = ry + 26;
  const boxW = (W - 12) / 2;
  const summary = (x, title, pairs) => {
    band(x, y, boxW, 14);
    text(title, x + 6, y + 10, { bold: true });
    let sy = y + 14;
    pairs.forEach(([k, v]) => {
      text(k, x + 6, sy + 8);
      text(v, x + boxW - 6, sy + 8, { align: "right" });
      sy += 10;
    });
    line(x, y, x, sy + 2, 0.5); line(x + boxW, y, x + boxW, sy + 2, 0.5); line(x, sy + 2, x + boxW, sy + 2, 0.5);
    return sy + 2;
  };
  const thisPeriod = [
    ["Gross Pay", money(grossPay)],
    ["Taxable Pay", money(grossPay)],
    ...(isContractor ? [] : [["PAYE Tax", money(payeTax)], ["National Insurance", money(nationalInsurance)]]),
    ...(Number(studentLoan) > 0 ? [["Student Loan", money(studentLoan)]] : []),
    ...(Number(totalContributions) > 0 ? [["Pension", money(totalContributions)]] : []),
    ...(Number(totalDeductions) > 0 ? [["Other Deductions", money(totalDeductions)]] : []),
    ["Net Pay", money(net)],
  ];
  const yearToDate = [
    ["Gross Pay", money(ytdGrossPay)],
    ["Taxable Pay", money(ytdGrossPay)],
    ...(isContractor ? [] : [["Tax Paid", money(ytdPayeTax)], ["National Insurance", money(ytdNationalInsurance)]]),
    ...(Number(ytdStudentLoan) > 0 ? [["Student Loan", money(ytdStudentLoan)]] : []),
    ...(Number(ytdContributions) > 0 ? [["Pension", money(ytdContributions)]] : []),
    ...(Number(ytdDeductions) > 0 ? [["Other Deductions", money(ytdDeductions)]] : []),
    ["Net Pay", money(ytdNet)],
  ];
  const b1 = summary(M, "This Period", thisPeriod);
  const b2 = summary(M + boxW + 12, `Year to Date${taxYear ? ` (${taxYear})` : ""}`, yearToDate);

  // ── net pay band ──
  y = Math.max(b1, b2) + 12;
  band(M, y, W, 24);
  text(pm.sentence, M + 8, y + 15, { size: 8.5 });
  text("NET PAY", R - 110, y + 16, { bold: true, size: 10, align: "right" });
  text(pounds(net), R - 8, y + 16, { bold: true, size: 13, align: "right" });

  // ── the frame, and the small print ──
  doc.setDrawColor(...rule);
  doc.setLineWidth(0.8);
  doc.rect(M, T, W, y + 24 - T);
  let fy = y + 38;
  if (hourly) { text(`Hours worked this period: ${(Number(hours || 0) + Number(overtime || 0)).toFixed(2)}`, M, fy, { size: 7.5, color: [90, 90, 90] }); fy += 10; }
  if (Number(totalContributions) > 0 || Number(ytdContributions) > 0) {
    text(`Employee pension this period ${pounds(totalContributions)}, year to date ${pounds(ytdContributions)}.`, M, fy, { size: 7.5, color: [90, 90, 90] });
  }
  if (ytdPayPeriods > 1) text(`Year to date covers ${ytdPayPeriods} pay periods since 6 April.`, R, fy, { size: 7.5, color: [90, 90, 90], align: "right" });
}
